import { useMutation, useQuery } from "@apollo/client/react";
import { useStepSave } from "../../../../hooks/useStepSave";
import { Box } from "@mui/material";
import { useVisita } from "../../../../../../context/VisitaContext";
import DataGridGenerico from '../../../../../../components/dataGrid/DataGridGenerico';
import { generateColumnsDefaultConfiguration } from "../../../../../../components/dataGrid/columnHelpers";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { GET_OPIOIDES, adaptOpioideArray } from '../../../../../../data/graphQL/queries/MisVisitas/getOpioides';
import { GET_COMBO_VISITA_VALOR, adaptCombosVisitas } from "../../../../../../data/graphQL/queries/MisVisitas/getCombosVisitaValor";
import { SAVE_OPIOIDES_VISITA, adaptCreateOpioidesVisitaInput } from '../../../../../../data/graphQL/mutations/Visitas/saveVisitaCP';
import { useEffect, useMemo, useState } from "react";

const ID_COMBO_VIA_ADMINISTRACION = 18;

export const TratamientoOpioides = ({ readOnly, onSaveRef, onExposeFormatted }) => {
  const [opioidesOptions, setOpioidesOptions] = useState([]);
  const [viaAdministracionOptions, setViaAdministracionOptions] = useState([]);
  const [formData, setFormData] = useState({});
  const [gridData, setGridData] = useState([]);

  const { data: dataOpiodes } = useQuery(GET_OPIOIDES);

	useEffect(() => {
		if (!dataOpiodes) return;
    setOpioidesOptions(adaptOpioideArray(dataOpiodes));
	}, [dataOpiodes]);

  const [save] = useMutation(SAVE_OPIOIDES_VISITA);

  const { data: dataComboViaAdm } = useQuery(GET_COMBO_VISITA_VALOR, {
    variables: { idComboVisita: ID_COMBO_VIA_ADMINISTRACION }
  });

	useEffect(() => {
		if (!dataComboViaAdm) return;
    setViaAdministracionOptions(adaptCombosVisitas(dataComboViaAdm));
	}, [dataComboViaAdm]);  

  useEffect(() => {
    setGridData(opioidesOptions.map((op) => {
      const found = formData?.opioides?.find((o) => o.idOpioide === op.idOpioide);
      if (found) return { ...op, ...found };
      else return { ...op };
    }));
  }, [formData.opioides]);

  useEffect(() => {
    setGridData(opioidesOptions);
  }, [opioidesOptions]);

  const { visita, setVisita } = useVisita();

  const handleChange = (ids) => {
    if (ids.length > 0) {
      setFormData((prev) => ({
        ...prev,
        'opioides': gridData.filter((item) => ids.includes(item.idOpioide))
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        'opioides': []
      }));
    }

  };

  const handleGridChange = (row, name, value) => {
    const itemExist = gridData.find((item) => item.idOpioide === row.idOpioide);
    if (!itemExist) {
      setGridData((prev) => [...prev, { ...row, [name]: value }]);
      setFormData((prev) => ({
        ...prev,
        'opioides': [...(prev.opioides || []), { ...row, [name]: value }]
      }));
    } else {
      setGridData((prev) => prev.map((item) => item.idOpioide === row.idOpioide ? { ...item, [name]: value } : item));
      setFormData((prev) => ({
        ...prev,
        'opioides': prev.opioides.map((item) => item.idOpioide === row.idOpioide ? { ...item, [name]: value } : item)
      }));
    }
  };

  const inputViaAdministracion = useMemo(() => {
    return {
      name: 'idComboOpioide',
      displayName: 'Seleccionar',
      type: 'select',
      options: viaAdministracionOptions,
      disabled: readOnly,
      required: false
    };
  }, [viaAdministracionOptions, readOnly]);

  const inputDosis = useMemo(() => {
    return {
      name: 'dosis',
      displayName: 'Dosis 24hs (mg)',
      type: 'int',
      disabled: readOnly,
      required: true
    };
  }, [readOnly]);

  const initialColumns = [
  {
    headerName: 'Opioide',
    field: 'opioide',
    width: 85
  },
  {
    headerName: '',
    field: 'action',
    width: 170,
    headerAlign: 'center',
    renderCell: (cellProps) => {
      const { row } = cellProps;
      inputViaAdministracion.disabled = disabledCheckbox(row) || !formData?.opioides || formData?.opioides?.some((op) => op.idOpioide === row.idOpioide) === false;
      return (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <RenderInputByTypeMobile
            key={'viaAdministracion'}
            input={inputViaAdministracion}
            value={formData?.opioides?.find((item) => item.idOpioide === row.idOpioide)?.idComboOpioide || row.idComboOpioide}
            handleChange={(_, value) => handleGridChange(row, 'idComboOpioide', value)} />          
        </Box>
      );
    }
  },
  {
    headerName: 'Vía de administración',
    field: 'idComboOpioide',
    width: 220,
    headerAlign: 'center',
    renderCell: (cellProps) => {
      const { row } = cellProps;
      inputDosis.disabled = disabledCheckbox(row) || !formData?.opioides || formData?.opioides?.some((op) => op.idOpioide === row.idOpioide) === false;
      inputDosis.displayName = row.idOpioide === 7 ? 'Dosis 24hs (msg)' : 'Dosis 24hs (mg)';
      return (
        <span>
          <Box sx={{ width: '80%', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <RenderInputByTypeMobile
              key={'dosis'}
              input={inputDosis}
              value={formData?.opioides?.find((item) => item.idOpioide === row.idOpioide)?.dosis || row.dosis}
              handleChange={(_, value) => handleGridChange(row, 'dosis', value)} />            
          </Box>
          mg
        </span>
      );
    }
  }];

  const columns = useMemo(
      () => generateColumnsDefaultConfiguration(initialColumns, true),
      [formData.opioides]
    );

  useStepSave(onSaveRef, async (validate) => {
    const { data } = await save({
      variables: { input: adaptCreateOpioidesVisitaInput(formData.idVisita, formData.opioides || []) }
    });

    setVisita((prev) => ({ ...prev, ...formData }));
  });

  useEffect(() => {
    setFormData(visita);
    if (visita.opioides && visita.opioides.length > 0) {
        setFormData((prev) => ({
          ...prev,
          'opioides': visita.opioides
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          'opioides': []
        }));
    }
  }, [visita]);

  const disabledCheckbox = (row) => {
    return row.idOpioide === 0 || readOnly;
  };

  return (
    <Box sx={{ width: '100%', mt: '1px', mb: '1px' }}>
      <DataGridGenerico
        columns={columns}
        rows={gridData}
        rowSelectionModel={formData?.opioides?.map((op) => op.idOpioide) || []}
        toolbar={false}
        hideFooter
        checkboxSelection={true}
        disableRowSelectionOnClick={true}
        isRowSelectable={(param) => !disabledCheckbox(param.row)}
        onRowSelectionModelChange={handleChange}
        rowHeight={60}
        disabledSelectionAll={readOnly}
        entityId={'idOpioide'} />      
    </Box>);
};